import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { useSnackbar } from 'notistack';
import {
  defaultBreakpoints,
  defaultWeights,
  normalizeWeights,
  presetWeights,
} from './indicatorConfig';
import {
  DEFAULT_CALCULATOR_DATASET_ID,
  entitiesForLevel,
  getCalculatorDataset,
  hasMultipleLevels,
} from './datasets';
import { rankEntities } from './scoringEngine';
import { coverageSummary, rankingDriver } from './insights';
import { parseCalculatorSearch, serializeCalculatorSearch } from './urlState';
import ScoringModelBar from './ScoringModelBar';
import InsightCards from './InsightCards';
import RankingTable from './RankingTable';
import EntityScoreDetail from './EntityScoreDetail';
import PartnerLogo from '../PartnerLogo';
import OpportunityCalculatorTour from './OpportunityCalculatorTour';
import {
  calculatorTourSteps,
  readCalculatorTourDismissed,
  shouldShowCalculatorTour,
  writeCalculatorTourDismissed,
} from './calculatorTour';

const numericBreakpoints = (breakpointsById, indicators) => {
  const next = {};
  indicators.forEach((indicator) => {
    const values = breakpointsById[indicator.id] || indicator.breakpoints;
    next[indicator.id] = values.map((value, index) =>
      Number.isFinite(Number(value)) ? Number(value) : indicator.breakpoints[index]
    );
  });
  return next;
};

const OpportunityCalculator = ({
  embedded = false,
  includeDataSources = embedded,
  isFullscreen = false,
  locationId = DEFAULT_CALCULATOR_DATASET_ID,
  onHighlightDataSources,
  onShare,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const dataset = useMemo(() => getCalculatorDataset(locationId), [locationId]);
  const calculatorData = dataset.data;
  const showLevelToggle = hasMultipleLevels(dataset);
  const [state] = useState(() => parseCalculatorSearch(searchParams, dataset));
  const [level, setLevel] = useState(state.level);
  const [weights, setWeights] = useState(state.weights);
  const [breakpointsById, setBreakpointsById] = useState(state.breakpointsById);
  const [minEnrollment, setMinEnrollment] = useState(state.minEnrollment);
  const [renormalize, setRenormalize] = useState(state.renormalize);
  const [autoNormalize, setAutoNormalize] = useState(state.autoNormalize);
  const [presetId, setPresetId] = useState('default');
  const [modelOpen, setModelOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showAllMetrics, setShowAllMetrics] = useState(false);
  const [coverageDismissed, setCoverageDismissed] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [tourStep, setTourStep] = useState(0);
  const [tourDismissed, setTourDismissed] = useState(() => readCalculatorTourDismissed());
  const [tourReplay, setTourReplay] = useState(false);

  const tourSteps = useMemo(
    () => calculatorTourSteps({ includeDataSources, includeLevel: showLevelToggle }),
    [includeDataSources, showLevelToggle]
  );
  const showingTour = shouldShowCalculatorTour({
    dismissed: tourDismissed,
    replaying: tourReplay,
    isFullscreen,
  });

  const levelConfig = dataset.levels[level];
  const indicators = levelConfig.indicators;
  const entities = entitiesForLevel(dataset, level);

  const defaultLevel = Object.keys(dataset.levels)[0];

  useEffect(() => {
    const next = serializeCalculatorSearch({
      level,
      indicators,
      defaultLevel,
      minEnrollment,
      renormalize,
      autoNormalize,
      weights,
      breakpointsById: numericBreakpoints(breakpointsById, indicators),
    });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    autoNormalize,
    breakpointsById,
    defaultLevel,
    indicators,
    level,
    minEnrollment,
    renormalize,
    searchParams,
    setSearchParams,
    weights,
  ]);

  const scoringWeights = useMemo(
    () => (autoNormalize ? normalizeWeights(weights) : weights),
    [autoNormalize, weights]
  );

  const ranked = useMemo(
    () =>
      rankEntities(entities, indicators, scoringWeights, {
        minEnrollment,
        enrollmentField: levelConfig.enrollmentField,
        renormalize,
        breakpointsById: numericBreakpoints(breakpointsById, indicators),
      }),
    [breakpointsById, entities, indicators, levelConfig.enrollmentField, minEnrollment, renormalize, scoringWeights]
  );

  const driver = useMemo(() => rankingDriver(ranked, indicators), [ranked, indicators]);
  const coverage = useMemo(() => coverageSummary(ranked, indicators), [ranked, indicators]);

  const selectedName = selectedRow?.entity?.name;
  useEffect(() => {
    if (!selectedName) return;
    const next = ranked.find((row) => row.entity.name === selectedName) || null;
    setSelectedRow(next);
  }, [ranked, selectedName]);

  useEffect(() => {
    setTourStep((current) => Math.min(current, Math.max(tourSteps.length - 1, 0)));
  }, [tourSteps]);

  useEffect(() => {
    const highlight = showingTour && tourSteps[tourStep]?.id === 'data-sources';
    onHighlightDataSources?.(highlight);
    return () => onHighlightDataSources?.(false);
  }, [showingTour, tourStep, tourSteps, onHighlightDataSources]);

  const goToTourStep = (nextStep) => {
    setTourStep(nextStep);
    setModelOpen(Boolean(tourSteps[nextStep]?.expandScoring));
  };

  const dismissTour = () => {
    writeCalculatorTourDismissed();
    setTourDismissed(true);
    setTourReplay(false);
    setTourStep(0);
  };

  const replayTour = () => {
    setTourStep(0);
    setTourReplay(true);
    setModelOpen(false);
  };

  const handleLevel = (_, nextLevel) => {
    if (!nextLevel || nextLevel === level) return;
    const nextIndicators = dataset.levels[nextLevel].indicators;
    setLevel(nextLevel);
    setWeights(defaultWeights(nextIndicators));
    setBreakpointsById(defaultBreakpoints(nextIndicators));
    setPresetId('default');
    setSearch('');
    setSelectedRow(null);
  };

  const handleWeightChange = (id, value) => {
    setWeights((current) => ({ ...current, [id]: value }));
    setPresetId('custom');
  };

  const handlePreset = (nextPresetId) => {
    setWeights(presetWeights(nextPresetId, indicators));
    setPresetId(nextPresetId);
  };

  const handleBreakpointChange = (id, values) => {
    setBreakpointsById((current) => ({ ...current, [id]: values }));
  };

  const copyLink = async () => {
    if (onShare) {
      onShare();
      return;
    }
    try {
      await navigator.clipboard.writeText(window.location.href);
      enqueueSnackbar('Link copied — weights and filters are in the URL.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Could not copy the link.', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ px: embedded ? { xs: 2, md: 3 } : 0, py: embedded ? 2.5 : 0 }}>
      <Container maxWidth="xl" disableGutters={embedded} sx={embedded ? undefined : { py: 3 }}>
      {!embedded && (
        <Button
          component={RouterLink}
          to="/city-explorer"
          startIcon={<ArrowBackIcon />}
          size="small"
          sx={{ mb: 1.5 }}
        >
          All locations
        </Button>
      )}

      <Box
        sx={{
          mb: 2.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {!embedded && (
            <Typography variant="h4" fontWeight={700}>
              Opportunity Calculator
            </Typography>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ mt: embedded ? 0 : 0.5 }}>
            Rank {dataset.copy.regionName} {levelConfig.entityNounPlural} by a weighted blend of population,
            enrollment, demographics, quality, and competition.
          </Typography>
        </Box>
        {!embedded && (
          <PartnerLogo locationId={locationId} size="header" sx={{ objectPosition: 'right center', flexShrink: 0, mt: 0.5 }} />
        )}
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2 }}
      >
        {showLevelToggle && (
          <Box data-calculator-tour="level">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={level}
              onChange={handleLevel}
              aria-label="Calculator level"
            >
              {Object.values(dataset.levels).map((levelOption) => (
                <ToggleButton key={levelOption.id} value={levelOption.id} sx={{ textTransform: 'none', px: 2 }}>
                  {levelOption.entityNounPlural.replace(/^./, (c) => c.toUpperCase())}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<LinkOutlinedIcon />}
          onClick={copyLink}
        >
          {onShare ? 'Share calculator' : 'Copy shareable link'}
        </Button>
        {tourDismissed && !showingTour && (
          <Button
            size="small"
            startIcon={<HelpOutlineIcon />}
            onClick={replayTour}
            aria-label="Replay calculator tour"
            sx={{ textTransform: 'none' }}
          >
            Tour
          </Button>
        )}
      </Stack>

      <Box data-calculator-tour="insights">
        <InsightCards
          driver={driver}
          coverage={coverageDismissed ? null : coverage}
          entityNounPlural={levelConfig.entityNounPlural}
          onDismissCoverage={() => setCoverageDismissed(true)}
        />
      </Box>

      <Box data-calculator-tour="scoring">
        <ScoringModelBar
          open={modelOpen}
          onToggle={() => setModelOpen((current) => !current)}
          indicators={indicators}
          weights={weights}
          onWeightChange={handleWeightChange}
          onPreset={handlePreset}
          presetId={presetId}
          minEnrollment={minEnrollment}
          onMinEnrollment={setMinEnrollment}
          autoNormalize={autoNormalize}
          onAutoNormalize={setAutoNormalize}
          renormalize={renormalize}
          onRenormalize={setRenormalize}
          breakpointsById={breakpointsById}
          onBreakpointChange={handleBreakpointChange}
          onBreakpointReset={() => setBreakpointsById(defaultBreakpoints(indicators))}
          entityCount={ranked.length}
          totalCount={entities.length}
        />
      </Box>

      <Box data-calculator-tour="table">
        <RankingTable
          rows={ranked}
          indicators={indicators}
          level={level}
          levelConfig={levelConfig}
          csvPrefix={dataset.copy.csvPrefix}
          search={search}
          onSearch={setSearch}
          showAllMetrics={showAllMetrics}
          onToggleAllMetrics={setShowAllMetrics}
          onSelect={setSelectedRow}
          selectedName={selectedRow?.entity?.name}
        />
      </Box>

      {calculatorData.meta?.notes?.[0] && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {calculatorData.meta.notes[0]}
        </Typography>
      )}

      <EntityScoreDetail
        row={selectedRow}
        level={level}
        renormalize={renormalize}
        onClose={() => setSelectedRow(null)}
      />
      {showingTour && (
        <OpportunityCalculatorTour
          steps={tourSteps}
          step={tourStep}
          onStepChange={goToTourStep}
          onDismiss={dismissTour}
          layoutKey={modelOpen}
        />
      )}
      </Container>
    </Box>
  );
};

export default OpportunityCalculator;
